(() => {
  if (!customElements.get('minimal-brand-wordmark')) {
    customElements.define('minimal-brand-wordmark', class extends HTMLElement {
      connectedCallback() {
        this.observer = new ResizeObserver(() => this.fit());
        this.observer.observe(this);
        this.fit();
        document.fonts?.ready.then(() => { if (this.isConnected) this.fit(); });
      }
      fit() {
        const text = this.querySelector('span');
        if (!text || !this.clientWidth) return;
        text.style.fontSize = '100px';
        const width = text.getBoundingClientRect().width;
        if (width) text.style.fontSize = `${Math.min(this.clientWidth * .3, 100 * this.clientWidth / width)}px`;
      }
      disconnectedCallback() { this.observer?.disconnect(); }
    });
  }
  if (customElements.get('minimal-product-story')) return;
  customElements.define('minimal-product-story', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.options = { signal: this.abort.signal };
      this.track = this.querySelector('.minimal-product-story__track');
      this.controls = this.querySelector('.minimal-product-story__controls');
      this.previous = this.querySelector('[data-story-previous]');
      this.next = this.querySelector('[data-story-next]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.queue = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
      };
      this.resize = new ResizeObserver(this.queue);
      this.resize.observe(this);
      if (this.track) {
        this.resize.observe(this.track);
        this.track.addEventListener('scroll', this.queue, { passive: true, signal: this.abort.signal });
        this.previous?.addEventListener('click', () => this.advance(-1), this.options);
        this.next?.addEventListener('click', () => this.advance(1), this.options);
      }
      window.addEventListener('resize', this.queue, this.options);
      this.motion.addEventListener('change', this.queue, this.options);
      document.fonts?.ready.then(() => { if (this.isConnected) this.queue(); });
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => this.prepare(), { once: true, signal: this.abort.signal });
      else this.prepare();
    }
    advance(direction) {
      const slide = this.track?.querySelector('.minimal-product-story__slide');
      if (!slide) return;
      const rtl = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
      this.track.scrollBy({ left: direction * rtl * (slide.getBoundingClientRect().width + 2), behavior: this.motion.matches ? 'instant' : 'smooth' });
    }
    prepare() {
      if (!this.isConnected) return;
      if (this.dataset.editor !== 'true' && !window.Shopify?.designMode && this.dataset.reveal === 'true') {
        const section = this.closest('.shopify-section');
        const main = document.getElementById('MainContent');
        if (section?.parentElement === main) {
          const following = Array.from(main.children).slice(Array.from(main.children).indexOf(section) + 1);
          if (following.length && following.every(node => node.classList.contains('shopify-section') && (node.querySelector('.minimal-newsletter') || node.querySelector('.minimal-footer')))) {
            this.endingSlot = document.createElement('div');
            this.endingSlot.className = 'minimal-product-ending-slot';
            this.panel = document.createElement('div');
            this.panel.className = 'minimal-product-ending-panel';
            this.endingSlot.append(this.panel);
            section.after(this.endingSlot);
            this.originals = following.map(node => {
              const marker = document.createElement('span');
              marker.hidden = true;
              node.before(marker);
              this.panel.append(node);
              return { node, marker };
            });
            this.resize.observe(this.panel);
            window.addEventListener('scroll', this.queue, { passive: true, signal: this.abort.signal });
            window.visualViewport?.addEventListener('resize', this.queue, this.options);
            this.panel.addEventListener('pointerdown', event => { if (this.isEditable(event.target)) this.pauseReveal(event.target); }, this.options);
            this.panel.addEventListener('focusin', event => {
              if (this.isEditable(event.target)) this.pauseReveal(event.target);
              else this.revealFocused(event.target);
            }, this.options);
            this.panel.addEventListener('focusout', this.queue, this.options);
          }
        }
      }
      this.queue();
    }
    geometry() {
      const viewport = window.innerHeight;
      const header = document.querySelector('.minimal-header');
      const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      const safeTop = Math.min(viewport - 1, Math.max(32, headerBottom + 16));
      const available = Math.max(1, viewport - safeTop);
      const height = this.panel.getBoundingClientRect().height;
      const slotTop = this.endingSlot.getBoundingClientRect().top;
      const distance = Math.max(1, Math.min(height, available));
      const baseTop = viewport - distance;
      const progress = Math.max(0, Math.min(1, (viewport - slotTop) / distance));
      const drift = (1 - progress) * Math.min(32, available * .06);
      const top = Math.min(baseTop, slotTop) + drift;
      const clip = slotTop >= viewport ? height : Math.max(0, Math.min(height, slotTop - top));
      return { viewport, safeTop, height, slotTop, baseTop, top, clip };
    }
    revealFocused(target) {
      if (!this.endingSlot.classList.contains('is-revealing') || !target.getBoundingClientRect) return;
      const geometry = this.geometry();
      const bounds = target.getBoundingClientRect();
      if (bounds.top >= Math.max(geometry.safeTop, geometry.slotTop) && bounds.bottom <= geometry.viewport - 16) return;
      const offset = bounds.top - this.panel.getBoundingClientRect().top;
      const extra = Math.max(0, offset + bounds.height - (geometry.viewport - geometry.baseTop - 16));
      window.scrollTo({ top: window.scrollY + geometry.slotTop - geometry.baseTop + extra, behavior: 'instant' });
    }
    isEditable(target) {
      return target?.matches?.('input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="submit"]):not([type="button"]), textarea, [contenteditable="true"]') || false;
    }
    pauseReveal(target) {
      if (this.inputViewportHeight || !this.endingSlot.classList.contains('is-revealing')) return;
      const before = target.getBoundingClientRect().top;
      this.inputViewportHeight = window.visualViewport?.height || window.innerHeight;
      this.inputViewportWidth = window.innerWidth;
      this.update(true);
      const movement = target.getBoundingClientRect().top - before;
      if (Math.abs(movement) > 1) window.scrollTo({ top: window.scrollY + movement, behavior: 'instant' });
      this.queue();
    }
    update(preserveInput = false) {
      if (this.track && this.controls) {
        const maximum = this.track.scrollWidth - this.track.clientWidth;
        const position = Math.abs(this.track.scrollLeft);
        this.controls.hidden = maximum <= 2;
        this.previous.disabled = position <= 2;
        this.next.disabled = position >= maximum - 2;
      }
      if (!this.endingSlot) return;
      if (this.inputViewportHeight) {
        const editing = this.panel.contains(document.activeElement) && this.isEditable(document.activeElement);
        const keyboardClosing = window.visualViewport && window.innerWidth === this.inputViewportWidth && window.visualViewport.height < this.inputViewportHeight - 80;
        if (!editing && !keyboardClosing && !preserveInput) this.inputViewportHeight = 0;
      }
      const geometry = this.geometry();
      const canReveal = !this.inputViewportHeight && !this.motion.matches && geometry.height > 0 && geometry.viewport > 0;
      this.endingSlot.style.setProperty('--minimal-ending-height', `${geometry.height}px`);
      this.endingSlot.classList.toggle('is-revealing', canReveal);
      if (canReveal) {
        this.endingSlot.style.setProperty('--minimal-ending-top', `${geometry.top}px`);
        this.endingSlot.style.setProperty('--minimal-ending-clip', `${geometry.clip}px`);
      }
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
      this.originals?.forEach(({ node, marker }) => { if (marker.parentNode) marker.replaceWith(node); });
      this.endingSlot?.remove();
      this.originals = null;
      this.endingSlot = null;
      this.panel = null;
      this.inputViewportHeight = 0;
      this.inputViewportWidth = 0;
      this.frame = 0;
    }
  });
})();
